import { Download, History, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { Button } from "@ceedero/ui";

import { useTranslation } from "@/localization/hooks/use-translation";
import { ProjectArtwork } from "@/music-catalog/components/project-detail-page/project-artwork";
import { ProjectMetaLine } from "@/music-catalog/components/project-detail-page/project-meta-line";
import {
  resolveProjectDownloadName,
  resolveProjectDownloadUrl,
} from "@/music-catalog/utils/catalog-view";
import { resolveProjectArtworkPanels } from "@/music-catalog/utils/project-artwork";
import { projectHistoryPagePath } from "@/project-history/utils/version-links";

import type { LibraryProject } from "@/cloud/types/types";

/**
 * The project, at the size it deserves.
 *
 * **There is no card here, and that is the design.** A panel around this put a
 * border, a radius and a second surface colour between the reader and the one
 * thing on the page they made; the album then sat in a box on a page, the way a
 * row sits in a table. Everything here is simply on the page — the artwork, the
 * name, what is true of it — and the album's own colour is what holds them
 * together instead of an outline.
 *
 * That colour is the same picture, scaled up and blurred past recognition, so
 * one project's page looks unlike another's without inventing a palette per
 * project and without anything here guessing at a colour or reading pixels to
 * find one. It bleeds off the top of the page and fades into the background
 * before it reaches the tracklist, so it has **no edge anywhere**: an earlier
 * attempt bounded it in a band, and the band's lower edge cut a hard line
 * straight across the middle of the disc.
 *
 * Two copies rather than one. A single blurred copy averages to one muddy
 * colour, much the same colour for most artwork, and every page ends up looking
 * alike — the thing the wash exists to avoid. The second is mirrored and turned,
 * so the two disagree across the page and leave a gradient between the album's
 * own colours rather than their average.
 *
 * The wash is an image rather than a CSS background, so no caller-supplied value
 * reaches the DOM through a style attribute.
 */
export const ProjectHero = ({
  duplicateCount,
  onRemove,
  project,
}: {
  duplicateCount: number;
  onRemove: () => void;
  project: LibraryProject;
}) => {
  const { t } = useTranslation();
  // The wash follows the album rather than whichever page is being looked at:
  // the colour of the page is the colour of the record, and having it change
  // under the reader as they flip between the cover and the disc would be motion
  // in aid of nothing.
  const [hasWashFailed, setWashFailed] = useState(false);
  const wash = resolveProjectArtworkPanels(project)[0];

  return (
    <div className="relative isolate">
      {wash !== undefined && !hasWashFailed && (
        /*
          The full width of the window, and not the width of the page.

          The glow has to be clipped somewhere — a blur this size would otherwise
          paint down over the tracklist — and wherever it is clipped there is an
          edge. Clipped to the page's own measure, those edges land in the middle
          of the window and the whole thing reads as a coloured rectangle sitting
          on the page, which is the one thing it must not look like. Taken to the
          window's width, the two vertical edges are off screen and the only ones
          left are the top and bottom, which the gradients below fade to the
          page's own background before they arrive.

          `w-screen` is safe here: the scroll container this page sits in clips
          its content, so the wider child is trimmed rather than given a
          horizontal scrollbar.

          The hero stacks on a narrow screen, so its wash is taller there and
          returns to the row-sized height at `lg`. The blurred pictures keep a
          minimum width on a phone: letting them shrink to half of a narrow
          viewport spreads their colour almost to transparency, while shrinking
          only the blur turns the wash into two obvious spots.
        */
        <div
          aria-hidden
          className="pointer-events-none absolute -top-28 left-1/2 -z-10 h-[44rem] w-screen -translate-x-1/2 overflow-hidden lg:h-144"
        >
          <img
            src={wash.url}
            alt=""
            className="absolute top-[-25%] left-1/2 aspect-square w-[max(52%,44rem)] -translate-x-1/2 object-cover opacity-45 blur-[110px] saturate-140 lg:left-[2%] lg:translate-x-0 dark:opacity-65 dark:saturate-150"
            onError={() => setWashFailed(true)}
          />
          {/*
            Centred with the first picture while the artwork is wider than a
            narrow viewport, so both sides receive the same amount of colour.
            At `lg` it moves further right and is turned, so the colour carries
            across the page rather than pooling behind the artwork. Two copies
            rather than one because a single blurred picture averages to one
            flat colour, and two that disagree leave a gradient between the
            album's own colours.
          */}
          <img
            src={wash.url}
            alt=""
            className="absolute top-[5%] left-1/2 aspect-square w-[max(55%,44rem)] -translate-x-1/2 scale-x-[-1] rotate-180 object-cover opacity-30 blur-[130px] saturate-160 lg:left-[30%] lg:translate-x-0 dark:opacity-45 dark:saturate-200"
          />
          {/*
            Top and bottom, both to the page's own background. The top fade is
            what lets the colour reach up behind the back link without swallowing
            it, and the bottom is what keeps it off the tracklist — each ends on
            the background exactly, so neither edge of the clip above is ever
            visible.
          */}
          <div className="absolute inset-0 bg-linear-to-b from-background/95 from-6% via-background/15 via-45% to-background dark:from-background/90 dark:via-transparent" />
        </div>
      )}

      <div className="flex flex-col items-center gap-8 pt-2 pb-4 lg:flex-row lg:items-center lg:gap-14 lg:pt-6">
        <ProjectArtwork project={project} />

        <div className="flex min-w-0 flex-col items-center gap-5 text-center lg:items-start lg:text-left">
          {/*
            The name is set at display scale and allowed to be long. An album is
            called what it is called, and "Techno House Selections, Volume 4" cut
            to fit a heading is a different record.
          */}
          <h1 className="font-display text-4xl leading-[1.05] font-semibold tracking-tight text-balance sm:text-5xl lg:text-6xl">
            {project.title}
          </h1>

          <ProjectMetaLine duplicateCount={duplicateCount} project={project} />

          <div className="mt-1 flex flex-wrap items-center justify-center gap-2 lg:justify-start">
            <Button asChild type="button" size="lg">
              <a
                href={resolveProjectDownloadUrl(project)}
                download={resolveProjectDownloadName(project)}
              >
                <Download />
                {t("catalog.download")}
              </a>
            </Button>
            <Button asChild type="button" variant="outline" size="lg">
              <Link to={projectHistoryPagePath(project.desktopProjectId)}>
                <History aria-hidden />
                {t("history.title")}
              </Link>
            </Button>
            {/*
              Quieter than the download beside it. Taking a copy is what somebody
              came to this page to do; removing the cloud's only copy is not, and
              the two must not read as a pair of equal choices.
            */}
            <Button type="button" variant="ghost" size="lg" onClick={onRemove}>
              <Trash2 />
              {t("catalog.remove")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
